import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { getNotifications, markAsRead, markAllAsRead, Notification } from "../services/notifications";
import { useSidebar } from "../app/App";
import { formatDate } from "../lib/dateFormat";

interface FrontendLayoutProps {
  children: React.ReactNode;
}

export default function FrontendLayout({ children }: FrontendLayoutProps) {
  const { user, activeRole, clubs, activeClubId, setActiveClubId, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleClubAdminMenu, clubPanelMounted } = useSidebar();

  // The club account has its own light top bar (ClubTopbar in ClubAdminLayout),
  // so the dark global header stands down wherever that layout is on screen.
  // The path check stays as the first frame's answer — the mounted flag only
  // arrives after the child's effect runs, and one frame of a second header is
  // still one too many.
  const isClubPanel =
    clubPanelMounted || location.pathname.startsWith("/dashboard/admin-club");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [clubMenuOpen, setClubMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const clubRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (clubRef.current && !clubRef.current.contains(event.target as Node)) {
        setClubMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications({ limit: 10 });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  // Load notifications on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Handle marking a notification as read
  const handleMarkAsRead = async (notification: Notification) => {
    if (!notification.read_at) {
      try {
        await markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
    setSelectedNotification(notification);
    setNotificationsOpen(false);
  };

  // Handle marking all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  // Format relative time
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("time.just_now", "just now");
    if (diffMins < 60) return t("time.minutes_ago", "{{count}} min ago", { count: diffMins });
    if (diffHours < 24) return t("time.hours_ago", "{{count}}h ago", { count: diffHours });
    if (diffDays < 7) return t("time.days_ago", "{{count}}d ago", { count: diffDays });
    return formatDate(date);
  };

  // Get current club name
  const currentClub = clubs?.find(c => c.id === activeClubId);
  const clubName = currentClub?.name || t("common.select_club", "Select Club");

  // Role badge color
  const getRoleBadgeColor = () => {
    switch (activeRole) {
      case "club_admin": return "bg-purple-500/20 text-purple-300 border-purple-500/30";
      case "coach": return "bg-amber-500/20 text-amber-300 border-amber-500/30";
      case "athlete": return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
      default: return "bg-slate-500/20 text-slate-300 border-slate-500/30";
    }
  };

  // Role label
  const getRoleLabel = () => {
    switch (activeRole) {
      case "club_admin": return t("roles.club_admin", "Club Admin");
      case "coach": return t("roles.coach", "Coach");
      case "athlete": return t("roles.athlete", "Athlete");
      default: return activeRole || "";
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // overflow-x-clip (NOT overflow-x-hidden): `hidden` creates a scroll
  // container which becomes the sticky containing block for the <header>
  // below, so the header never pins. `clip` blocks horizontal overflow
  // without becoming a scroll container, so position: sticky still works.
  return (
    <div className="min-h-screen overflow-x-clip bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 rounded-full left-1/4 w-96 h-96 bg-emerald-500/10 filter blur-3xl"></div>
        <div className="absolute bottom-0 rounded-full right-1/4 w-96 h-96 bg-blue-500/10 filter blur-3xl"></div>
      </div>

      <div className="bg-white px-3 py-3 flex flex-wrap items-center gap-3 justify-between">
        <img src="/assets/cofinantare.png" alt="Confinantare Uniunea Europeana" className="h-10 sm:h-12" />
        <img src="/assets/gov_ro.svg" alt="Guvernul Romaniei" className="h-10 sm:h-12" />
      </div>

      {/* Impersonation banner.
          Deliberately NOT sticky. It used to be, pinned at top:0 with
          z-[70] — the same offset as the sticky header and the club top bar,
          so it overlapped them, and it outranked every dialog in the app: the
          bottom of a modal disappeared behind it. Left in normal flow it has
          no one to fight: prominent on load, scrolls away like the rest of the
          page, and the way back to your own account is one scroll up. */}
      <ImpersonationBanner />

      {/* Header/Navbar — hidden on club routes, where ClubAdminLayout renders
          its own light ClubTopbar (language, notifications, account) instead. */}
      {!isClubPanel && (
      <header className="sticky top-0 z-50 border-b bg-slate-900/80 backdrop-blur-xl border-slate-700/50">
        <div className="px-4 mx-auto">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo */}
            <div className="flex items-center gap-3 min-w-0">
              {/* Hamburger — only for club_admin on mobile; toggles ClubAdminLayout's
                  sidebar via the shared SidebarContext so the burger lives in the
                  <header>, not in <main>. */}
              {activeRole === "club_admin" && (
                <button
                  onClick={toggleClubAdminMenu}
                  className="lg:hidden p-2 -ml-1 rounded-lg hover:bg-slate-800/50 text-slate-200 transition-colors"
                  aria-label={t("common.open_menu", "Deschide meniul")}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              <Link to="/dashboard" className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-white">
                  <span className="text-emerald-400">i</span>GROWth
                </h1>
              </Link>


              {/* Role badge */}
              <span className={`hidden sm:inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor()}`}>
                {getRoleLabel()}
              </span>
            </div>

            {/* Center: Club selector (desktop) */}
            <div className="items-center hidden md:flex" ref={clubRef}>
              {clubs && clubs.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setClubMenuOpen(!clubMenuOpen)}
                    className="flex items-center gap-2 px-4 py-2 transition-all border rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border-slate-700/50"
                  >
                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="text-white font-medium max-w-[200px] truncate">{clubName}</span>
                    <svg className={`w-4 h-4 text-slate-400 transition-transform ${clubMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Club dropdown */}
                  {clubMenuOpen && (
                    <div className="absolute left-0 w-64 mt-2 overflow-hidden border shadow-xl top-full bg-slate-800 rounded-xl border-slate-700/50">
                      <div className="p-2">
                        {clubs.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setActiveClubId(c.id);
                              setClubMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                              c.id === activeClubId
                                ? "bg-emerald-500/20 text-emerald-300"
                                : "text-slate-300 hover:bg-slate-700/50"
                            }`}
                          >
                            {c.logo_url ? (
                              <img src={c.logo_url} alt="" className="object-cover w-8 h-8 rounded-lg" />
                            ) : (
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-700">
                                <span className="text-sm font-bold text-slate-400">{c.name.charAt(0)}</span>
                              </div>
                            )}
                            <span className="font-medium truncate">{c.name}</span>
                            {c.id === activeClubId && (
                              <svg className="w-4 h-4 ml-auto text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language switcher — always visible (including mobile),
                  user wants the picker reachable from the top header. */}
              <LanguageSwitcher />

              {/* Notifications dropdown */}
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 transition-all border rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border-slate-700/50"
                >
                  <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {/* Notification badge */}
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications dropdown menu */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 overflow-hidden border shadow-xl top-full w-80 sm:w-96 bg-slate-800 rounded-xl border-slate-700/50">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
                      <h3 className="font-semibold text-white">{t("notifications.title", "Notifications")}</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="text-xs transition-colors text-emerald-400 hover:text-emerald-300"
                        >
                          {t("notifications.mark_all_read", "Mark all read")}
                        </button>
                      )}
                    </div>

                    {/* Notifications list */}
                    <div className="overflow-y-auto max-h-96">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <svg className="w-12 h-12 mx-auto mb-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <p className="text-sm text-slate-500">{t("notifications.empty", "No notifications yet")}</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <button
                            key={notification.id}
                            onClick={() => handleMarkAsRead(notification)}
                            className={`w-full px-4 py-3 text-left border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors ${
                              !notification.read_at ? "bg-slate-700/20" : ""
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Unread indicator */}
                              <div className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${
                                !notification.read_at ? "bg-emerald-500" : "bg-transparent"
                              }`}></div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${
                                  !notification.read_at ? "text-white" : "text-slate-300"
                                }`}>
                                  {notification.title || notification.template_code}
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                                  {notification.payload?.message || ""}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {formatTimeAgo(notification.created_at)}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-2.5 border-t border-slate-700/50">
                        <Link
                          to="/notifications"
                          className="text-sm transition-colors text-emerald-400 hover:text-emerald-300"
                          onClick={() => setNotificationsOpen(false)}
                        >
                          {t("notifications.view_all", "View all notifications")}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 p-1.5 pr-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 transition-all"
                >
                  {/* Avatar */}
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-500">
                    <span className="text-sm font-bold text-white">
                      {user?.full_name?.charAt(0) || user?.email?.charAt(0) || "?"}
                    </span>
                  </div>
                  <span className="hidden sm:block text-white font-medium max-w-[120px] truncate">
                    {user?.full_name || user?.email?.split("@")[0]}
                  </span>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${profileMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Profile dropdown menu */}
                {profileMenuOpen && (
                  <div className="absolute right-0 w-56 mt-2 overflow-hidden border shadow-xl top-full bg-slate-800 rounded-xl border-slate-700/50">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-slate-700/50">
                      <p className="font-medium text-white truncate">{user?.full_name || "User"}</p>
                      <p className="text-sm truncate text-slate-400">{user?.email}</p>
                    </div>

                    {/* Mobile: Club selector */}
                    <div className="p-2 border-b md:hidden border-slate-700/50">
                      <p className="px-2 py-1 text-xs font-medium uppercase text-slate-500">{t("common.club", "Club")}</p>
                      {clubs?.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setActiveClubId(c.id);
                            setProfileMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm ${
                            c.id === activeClubId
                              ? "bg-emerald-500/20 text-emerald-300"
                              : "text-slate-300 hover:bg-slate-700/50"
                          }`}
                        >
                          <span className="truncate">{c.name}</span>
                        </button>
                      ))}
                    </div>

                    {/* Menu items */}
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setProfileMenuOpen(false);
                          if (user?.id) navigate(`/admin/users/${user.id}`);
                        }}
                        className="flex items-center w-full gap-3 px-3 py-2 transition-colors rounded-lg text-slate-300 hover:bg-slate-700/50"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>{t("common.profile", "Profile")}</span>
                      </button>

                      <hr className="my-2 border-slate-700/50" />

                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full gap-3 px-3 py-2 text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>{t("common.logout", "Logout")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Main content */}
      <main className="relative mx-auto mobile:p-0">
        {children}
      </main>

      {/* Footer — hidden on club routes, where ClubAdminLayout renders the
          EU co-financing note small and grey at the bottom of <main>. */}
      {!isClubPanel && (
      <footer className="relative mt-8 border-t border-slate-800/50">
        <div className="block p-4 pb-4 my-4 font-semibold text-center text-slate-300 text-xxs">
          <p>Această platformă web și aplicație mobilă au fost realizate cu sprijinul financiar al Uniunii Europene prin Programul Educației și Ocupare 2021-2027, cofinanțat din FSE+.<br/>
          Conținutul acestui material nu reprezintă în mod obligatoriu poziția oficială a Uniunii Europene sau a Guvernului României.<br/>
          Pentru informații detaliate despre celalalte programe finanțate de Uniunea Europeană, vă rugăm să vizitați <a href="https://www.fonduri-ue.ro" target="_blank" rel="nofollow">www.fonduri-ue.ro</a></p>
        </div>
      </footer>
      )}

      {/* Notification detail modal */}
      {selectedNotification && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setSelectedNotification(null)}
        >
          <div
            className="w-full max-w-lg overflow-hidden border shadow-2xl bg-slate-800 rounded-2xl border-slate-700/50"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-white">
                {selectedNotification.title || selectedNotification.template_code}
              </h3>
              <button
                onClick={() => setSelectedNotification(null)}
                className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors"
              >
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal content */}
            <div className="px-6 py-4">
              <p className="whitespace-pre-wrap text-slate-300">
                {selectedNotification.payload?.message || t("notifications.no_message", "No message content")}
              </p>

              {/* Metadata */}
              <div className="flex flex-wrap gap-4 pt-4 mt-4 text-xs border-t border-slate-700/30 text-slate-500">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{new Date(selectedNotification.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${selectedNotification.read_at ? "bg-slate-500" : "bg-emerald-500"}`}></span>
                  <span>{selectedNotification.read_at ? t("notifications.read", "Read") : t("notifications.unread", "Unread")}</span>
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 px-6 py-3 border-t border-slate-700/50">
              {selectedNotification.payload?.action_url && (
                <a
                  href={selectedNotification.payload.action_url}
                  className="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-emerald-600 hover:bg-emerald-700"
                >
                  {t("notifications.view_details", "View Details")}
                </a>
              )}
              <button
                onClick={() => setSelectedNotification(null)}
                className="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-slate-700 hover:bg-slate-600"
              >
                {t("common.close", "Close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
